import { parse } from '@babel/parser'
import traverse from '@babel/traverse'
import { JSXElement } from '@babel/types'
import { RouteConfig } from '@/lib/types'

export function extractRoutes(code: string): RouteConfig[] {
  try {
    const ast = parse(code, {
      sourceType: 'module',
      plugins: ['jsx', 'typescript'],
    })

    const flatRoutes: RouteConfig[] = []

    function extractFromJSX(
      node: JSXElement,
      parentPath = '',
      parentElement?: string,
    ) {
      const route = extractRouteFromJSX(node)
      const currentPath = route ? resolvePath(parentPath, route) : parentPath

      // 过滤 auth/callback 路由
      if (
        currentPath.includes('/auth/callback') ||
        currentPath.includes('/checkout/cancel') ||
        currentPath.includes('/checkout/error') ||
        currentPath.includes('/login')
      ) {
        return
      }

      // 查找子节点是否包含 index route
      let indexElement: string | undefined
      const childRoutes: JSXElement[] = []

      node.children.forEach((child) => {
        if (
          child.type === 'JSXElement' &&
          child.openingElement.name.type === 'JSXIdentifier' &&
          child.openingElement.name.name === 'Route'
        ) {
          const childRoute = extractRouteFromJSX(child)
          if (childRoute?.index && childRoute.element) {
            indexElement = childRoute.element // 用作父 element
          } else {
            childRoutes.push(child) // 递归处理其他子 Route
          }
        }
      })

      // 替换父 element
      const effectiveElement = indexElement ?? route?.element ?? parentElement

      if (route && effectiveElement) {
        flatRoutes.push({
          path: currentPath,
          element: effectiveElement,
        })
      }

      // 递归处理子路由
      childRoutes.forEach((child) => {
        extractFromJSX(child, currentPath)
      })
    }

    traverse(ast, {
      JSXElement(path) {
        const node = path.node
        const name = node.openingElement.name
        if (name.type === 'JSXIdentifier' && name.name === 'Route') {
          const isTopLevel =
            !path.parentPath || path.parentPath.node.type !== 'JSXElement'
          const parentName =
            path.parentPath?.node.type === 'JSXElement'
              ? path.parentPath.node.openingElement.name
              : null

          if (
            isTopLevel ||
            (parentName?.type === 'JSXIdentifier' &&
              parentName.name !== 'Route')
          ) {
            extractFromJSX(node)
          }
        }
      },
    })

    return flatRoutes
  } catch (error) {
    console.error('Error extracting routes:', error)
    return []
  }
}

function extractRouteFromJSX(node: JSXElement): RouteConfig | null {
  const route = {} as RouteConfig
  const openingElement = node.openingElement

  // node 至少要有 path 和 index 属性，如果都没有，直接过滤掉
  let hasPath = false
  let isIndex = false

  openingElement.attributes.forEach((attr) => {
    if (attr.type !== 'JSXAttribute') return

    const name = attr.name.name
    const value = attr.value

    if (name === 'path' && value?.type === 'StringLiteral') {
      route.path = value.value
      hasPath = true
    }

    if (name === 'index') {
      route.index = true
      isIndex = true
    }

    if (name === 'element' && value?.type === 'JSXExpressionContainer') {
      // 标准节点 element={<App />}
      if (value.expression.type === 'JSXElement') {
        const el = value.expression.openingElement.name
        if (el.type === 'JSXIdentifier') {
          const elementName = el.name

          // Check if this is AuthProtect wrapper with a child component
          if (
            elementName === 'AuthProtect' &&
            value.expression.children &&
            value.expression.children.length > 0
          ) {
            // Look for the actual component inside AuthProtect
            for (const child of value.expression.children) {
              if (child.type === 'JSXElement') {
                const childName = child.openingElement.name
                if (childName.type === 'JSXIdentifier') {
                  // Use the child component name (like AdminDashboard)
                  route.element = childName.name
                  break
                }
              }
            }
            // If no child component found, fall back to AuthProtect
            if (!route.element) {
              route.element = elementName
            }
          } else {
            route.element = elementName
          }
        }
      } else if (value.expression.type === 'JSXFragment') {
        // 空标准节点 path="/child" element={<><Child /></>}，直接用 path 作为节点名
        const removePrefixPath = (route.path || '').replace(/^\/+/g, '')
        route.element = removePrefixPath === '' ? 'Home' : removePrefixPath
      }
    }
  })

  if (!hasPath && !isIndex) {
    return null
  }

  return route
}

function resolvePath(parentPath: string, route: RouteConfig): string {
  if (route.index) {
    return parentPath || '/'
  }

  const rawPath = route.path || ''
  if (!parentPath) {
    return rawPath.startsWith('/') ? rawPath : '/' + rawPath
  }

  return `${parentPath.replace(/\/$/, '')}/${rawPath}`.replace(/\/+/g, '/')
}
